// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useNavigate } from '@tanstack/react-router';

// material ui icons
import LanRounded from '@mui/icons-material/LanRounded';

// material ui
import {
  alpha,
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
} from '@mui/material';

// shared components
import { AddButton } from '@/components/ui/button';

interface CreateFirstDeepdiveCardProps {
  accountId: string | undefined;
}

const CreateFirstDeepdiveCard = ({
  accountId,
}: CreateFirstDeepdiveCardProps) => {
  const { t } = useTranslation('energy-analysis');
  const navigate = useNavigate();

  const highlightKeys = t('portfolio.deepdive_list.empty_state.highlights', {
    returnObjects: true,
    defaultValue: [],
  }) as string[];

  const handleCreateClick = () => {
    if (!accountId) return;

    navigate({
      to: `/${accountId}/energyxplorer/deepdive/create`,
    });
  };

  return (
    <Card
      variant="outlined"
      data-testid="create-first-deepdive-card"
      sx={{
        height: '100%',
        minHeight: 320,
        overflow: 'hidden',
        position: 'relative',
        borderColor: 'divider',
      }}
    >
      <CardContent
        sx={{
          height: '100%',
          p: { xs: 3, md: 4 },
          position: 'relative',
        }}
      >
        <Stack
          spacing={3}
          justifyContent="space-between"
          sx={{ height: '100%' }}
        >
          <Stack spacing={2.5}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 3,
                display: 'grid',
                placeItems: 'center',
                color: 'success.dark',
                bgcolor: (theme) => alpha(theme.palette.success.main, 0.14),
                boxShadow: (theme) =>
                  `inset 0 0 0 1px ${alpha(theme.palette.success.main, 0.12)}`,
              }}
            >
              <LanRounded />
            </Box>

            <Stack spacing={1} maxWidth={560}>
              <Typography variant="h5" fontWeight={700}>
                {t('portfolio.deepdive_list.empty_state.title')}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {t('portfolio.deepdive_list.empty_state.description')}
              </Typography>
            </Stack>

            <Box display="flex" flexWrap="wrap" gap={1}>
              {highlightKeys.map((key) => (
                <Chip
                  key={key}
                  label={t(key)}
                  color="default"
                  variant="outlined"
                  sx={{
                    bgcolor: (theme) =>
                      alpha(theme.palette.background.paper, 0.72),
                  }}
                />
              ))}
            </Box>
          </Stack>

          <Box display="flex" justifyContent="flex-start">
            <AddButton
              onClick={handleCreateClick}
              disabled={!accountId}
              data-testid="create-first-deepdive-button"
            >
              {t('portfolio.deepdive_list.empty_state.action')}
            </AddButton>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default CreateFirstDeepdiveCard;
